import { z } from 'zod';

import { ImpressionDeviceValueSchema } from '../constants/impression-device.js';
import { AdvancedFiltersSchema, DashboardMode } from '../events/workspace.js';
import {
  CompetitorPresetGlobalFiltersSchema,
  FilterPresetScopeSchema,
  MyBrandPresetGlobalFiltersSchema,
} from '../overview/filter-presets.js';

const DRILLDOWN_VIEW_MODES = ['grid', 'list', 'chart'] as const;
const COMPETITOR_SORT_MODES = ['Days', 'Assets', 'Days/Assets', 'Date'] as const;
const SORT_DIRECTIONS = ['asc', 'desc'] as const;

/** Competitors drilldown page filters (excludes derived absolute dates). */
export const DrilldownCompetitorsPagePresetFiltersSchema = z.object({
  objectType: z.array(z.string()).default([]),
  effectiveStatus: z.array(z.string()).default([]),
  segment1: z.string().default('Hash'),
  segment2: z.string().default('All'),
  segment3: z.string().default('All'),
  advancedFilters: AdvancedFiltersSchema.optional().default([]),
  competitorSortMode: z.enum(COMPETITOR_SORT_MODES).default('Days/Assets'),
  competitorSortDirection: z.enum(SORT_DIRECTIONS).default('desc'),
  viewMode: z.enum(DRILLDOWN_VIEW_MODES).default('grid'),
});
export type DrilldownCompetitorsPagePresetFilters = z.infer<
  typeof DrilldownCompetitorsPagePresetFiltersSchema
>;

/** My Brand drilldown page filters (excludes derived absolute dates). */
export const DrilldownMyBrandPagePresetFiltersSchema = z.object({
  myBrandObjectType: z.array(z.string()).default([]),
  myBrandEffectiveStatus: z.array(z.string()).default([]),
  audienceSegment: z.array(z.string()).default([]),
  device: z.array(ImpressionDeviceValueSchema).default([]),
  aspectRatio: z.array(z.string()).default([]),
  myBrandSegment1: z.string().default('Hash'),
  myBrandSegment2: z.string().default('All'),
  myBrandSegment3: z.string().default('All'),
  myBrandAdvancedFilters: AdvancedFiltersSchema.optional().default([]),
  myBrandKpis: z.array(z.string()).default(['spend']),
  myBrandSortMode: z.string().default('spend'),
  myBrandSortDirection: z.enum(SORT_DIRECTIONS).default('desc'),
  /** List-view date columns the user hid (`min_date`, `max_date`, `days`). */
  myBrandHiddenColumns: z.array(z.string()).optional().default([]),
  viewMode: z.enum(DRILLDOWN_VIEW_MODES).default('grid'),
});
export type DrilldownMyBrandPagePresetFilters = z.infer<
  typeof DrilldownMyBrandPagePresetFiltersSchema
>;

export const DrilldownFilterPresetPayloadSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('my-brand'),
    page: DrilldownMyBrandPagePresetFiltersSchema,
    global: MyBrandPresetGlobalFiltersSchema,
  }),
  z.object({
    mode: z.literal('competitors'),
    page: DrilldownCompetitorsPagePresetFiltersSchema,
    global: CompetitorPresetGlobalFiltersSchema,
  }),
]);
export type DrilldownFilterPresetPayload = z.infer<typeof DrilldownFilterPresetPayloadSchema>;

export const CreateDrilldownFilterPresetRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
  })
  .and(DrilldownFilterPresetPayloadSchema);
export type CreateDrilldownFilterPresetRequest = z.infer<
  typeof CreateDrilldownFilterPresetRequestSchema
>;

export const DrilldownFilterPresetDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  mode: DashboardMode,
  filters: DrilldownFilterPresetPayloadSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  scope: FilterPresetScopeSchema,
  canManage: z.boolean(),
  canDelete: z.boolean(),
  canReorder: z.boolean(),
});
export type DrilldownFilterPresetDto = z.infer<typeof DrilldownFilterPresetDtoSchema>;

export const ListDrilldownFilterPresetsQuerySchema = z.object({
  mode: DashboardMode,
  includeCompany: z
    .string()
    .optional()
    .transform((value) => value === 'true' || value === '1'),
});
export type ListDrilldownFilterPresetsQuery = z.infer<typeof ListDrilldownFilterPresetsQuerySchema>;

export {
  ReorderFilterPresetsBodySchema,
  FilterPresetScopeSchema,
  type ReorderFilterPresetsBody,
  type FilterPresetScope,
} from '../overview/filter-presets.js';
